import React, { useState, useEffect, useCallback, useMemo } from "react";
import { InputAdornment, TextField } from "@material-ui/core";
import { Search as SearchIcon } from "@material-ui/icons";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { makeStyles } from "@material-ui/core/styles";
import { useDebounce } from "src/utils";

const useStyles = makeStyles(_ => ({
  searchTerm: {
    "& .MuiOutlinedInput-adornedEnd": {
      paddingRight: "16px !important"
    }
  }
}));

interface Props<T> {
  options: IDropdown<T>[];
  label: string;
  value?: IDropdown<T> | null;
  onChange: (value: IDropdown<T> | null) => void;
  excludedDropdownValues?: string[];
  disabled?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
}

export default function SimpleDropdown<T>(props: Props<T>) {
  const {
    options,
    label,
    onChange,
    value,
    excludedDropdownValues,
    disabled,
    error,
    helperText
  } = props;
  const classes = useStyles();

  const [open, setOpen] = React.useState(false);
  const [filteredOptions, setFilteredOptions] = useState<IDropdown<T>[]>([]);

  const [searchTerm, setSearchTerm] = useState("");
  const { value: debouncedSearchTerm } = useDebounce(searchTerm, 300);
  const excluded = useMemo(
    () => new Set(excludedDropdownValues ?? []),
    [excludedDropdownValues]
  );

  const handleChangeInput = (
    _: React.ChangeEvent<unknown>,
    changedSearchTerm: string
  ) => {
    setSearchTerm(changedSearchTerm);
  };

  const handleOnChange = (
    _: React.ChangeEvent<unknown>,
    changedValue: IDropdown<T> | null
  ) => {
    onChange(changedValue);
  };

  const handleFilterOptions = useCallback(
    async (term?: string) => {
      const query = term?.trim()?.toUpperCase();
      setFilteredOptions(
        options.filter(
          op =>
            !excluded.has(op.value) &&
            (!query || op.label.toUpperCase().includes(query))
        )
      );
    },
    [options, excluded]
  );

  useEffect(() => {
    if (open) {
      handleFilterOptions(debouncedSearchTerm);
    }
  }, [open, handleFilterOptions, debouncedSearchTerm]);

  return (
    <Autocomplete
      fullWidth
      open={open}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
      }}
      getOptionSelected={(option, optionValue) =>
        option.value === optionValue.value
      }
      getOptionLabel={option => option.label}
      disabled={disabled}
      options={filteredOptions}
      onInputChange={handleChangeInput}
      onChange={handleOnChange}
      value={value}
      renderInput={params => (
        <TextField
          className={classes.searchTerm}
          {...params}
          disabled={disabled}
          label={label}
          error={error}
          helperText={helperText}
          variant="outlined"
          placeholder={`พิมพ์เพื่อค้นหา ${label}`}
          value=""
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon />
              </InputAdornment>
            )
          }}
        />
      )}
    />
  );
}
